// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import './ComingSoon.scss';

interface ComingSoonProps {
  title?: string;
  subtitle?: string;
  description?: string;
  launchDate?: Date;
  showCountdown?: boolean;
  showNotifyButton?: boolean;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ComingSoon: React.FC<ComingSoonProps> = ({
  title = "Coming Soon",
  subtitle = "Something Amazing is on the Way",
  description = "We're working hard to bring you an incredible new experience. Stay tuned for updates!",
  launchDate,
  showCountdown = true,
  showNotifyButton = true
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!launchDate || !showCountdown) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate, showCountdown]);

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 1500);
  };

  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  return (
    <div className="coming-soon">
      {/* Animated Background */}
      <div className="coming-soon-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="coming-soon-content">
        <div className="coming-soon-container">
          {/* Logo/Icon */}
          <div className="coming-soon-icon">
            <div className="icon-wrapper">
              🚀
            </div>
          </div>

          {/* Title Section */}
          <div className="coming-soon-header">
            <h1 className="coming-soon-title">{title}</h1>
            <h2 className="coming-soon-subtitle">{subtitle}</h2>
            <p className="coming-soon-description">{description}</p>
          </div>

          {/* Countdown Timer */}
          {showCountdown && launchDate && (
            <div className="countdown-section">
              <h3 className="countdown-label">Launch Countdown</h3>
              <div className="countdown-timer">
                <div className="countdown-item">
                  <div className="countdown-number">{formatNumber(timeLeft.days)}</div>
                  <div className="countdown-label-small">Days</div>
                </div>
                <div className="countdown-separator">:</div>
                <div className="countdown-item">
                  <div className="countdown-number">{formatNumber(timeLeft.hours)}</div>
                  <div className="countdown-label-small">Hours</div>
                </div>
                <div className="countdown-separator">:</div>
                <div className="countdown-item">
                  <div className="countdown-number">{formatNumber(timeLeft.minutes)}</div>
                  <div className="countdown-label-small">Minutes</div>
                </div>
                <div className="countdown-separator">:</div>
                <div className="countdown-item">
                  <div className="countdown-number">{formatNumber(timeLeft.seconds)}</div>
                  <div className="countdown-label-small">Seconds</div>
                </div>
              </div>
            </div>
          )}

          {/* Notify Me Section */}
          {showNotifyButton && (
            <div className="notify-section">
              {!isSubscribed ? (
                <form onSubmit={handleNotifySubmit} className="notify-form">
                  <h3 className="notify-title">Get Notified When We Launch</h3>
                  <div className="notify-input-group">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="notify-input"
                      required
                    />
                    <button 
                      type="submit" 
                      className="notify-button"
                      disabled={isLoading || !email}
                    >
                      {isLoading ? (
                        <div className="loading-spinner">
                          <div className="spinner"></div>
                        </div>
                      ) : (
                        <>
                          <span>Notify Me</span>
                          <span className="button-icon">📧</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="success-message">
                  <div className="success-icon">✅</div>
                  <h3 className="success-title">You're All Set!</h3>
                  <p className="success-text">We'll notify you as soon as we launch. Thanks for your interest!</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
